export function capitalizeString(inputString?: string) {
  if (!inputString) return "";
  return inputString
    ? inputString.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
}

/**
 * Converts a snake_case string into space-separated capitalized words.
 *
 * @param input - The snake_case string to convert; nullish values yield an empty string
 * @returns The capitalized, space-separated string
 */
export function capitalizeWordsSeperatedByUnderScore(
  input?: string | null,
): string {
  const words = (input ?? "").split("_");
  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1),
  );
  const result = capitalizedWords.join(" ");
  return result;
}

/**
 * Capitalizes the first letter of each word in a string separated by spaces.
 *
 * @param {string} input - The input string to be capitalized.
 * @returns {string} - The capitalized string.
 */
export function capitalizeWordsSeparatedBySpace(input: string): string {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function replaceSemicolonWithBr(text) {
  return text.replace(/;/g, "<br />");
}

export function replaceBrWithSemicolon(text) {
  return text.replace(/<br\s*\/?>/gi, ";");
}

/**
 * Format a raw phone value into a US-style `(XXX) XXX-XXXX` string.
 *
 * Accepts non-string inputs (number, null, undefined) defensively by coercing
 * to a string first, so callers passing numeric or empty values won't throw.
 *
 * @param value - The raw phone value to format
 * @returns The progressively formatted phone number string
 */
export const formatPhoneNumber = (
  value: string | number | null | undefined,
) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const unformatPhoneNumber = (formattedValue: string) => {
  return formattedValue.replace(/\D/g, ""); // Remove all non-digit characters
};

export const formatPhoneNumberWithCountryCode = (value: string) => {
  const digits = value.replace(/\D/g, "");
  // Handle different country codes
  if (digits.startsWith("1") && digits.length > 10) {
    // USA/Canada (Country Code +1)
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  } else if (digits.startsWith("44") && digits.length > 10) {
    // UK (Country Code +44)
    return `+44 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 12)}`;
  } else if (digits.startsWith("91") && digits.length >= 10) {
    // India (Country Code +91) - Corrected format
    return `+91 ${digits.slice(2, 6)}-${digits.slice(6, 12)}`;
  } else if (digits.length > 10) {
    // Generic International Format
    return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
  }

  // Default formatting for 10-digit numbers (assumed US format)
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Digit count of a NANP subscriber number (`XXX-XXX-XXXX`). */
const NANP_SUBSCRIBER_DIGIT_COUNT = 10;

/**
 * Standardizes a fax number for persistence as `+1-XXX-XXX-XXXX`.
 *
 * Non-digits are stripped first. A leading country-code `1` is dropped when
 * the remaining length is 10 (e.g. `11234567890` and `+1 (123) 456-7890`
 * both become `+1-123-456-7890`). Incomplete values keep whatever subscriber
 * digits are present so the caller can still display a partial number.
 *
 * @param value - Raw or already-formatted fax number
 * @returns Fax number in `+1-XXX-XXX-XXXX` form
 */
export const formatFaxNumberForSave = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const subscriberDigits =
    digits.length > NANP_SUBSCRIBER_DIGIT_COUNT && digits.startsWith("1")
      ? digits.slice(1, 1 + NANP_SUBSCRIBER_DIGIT_COUNT)
      : digits.slice(0, NANP_SUBSCRIBER_DIGIT_COUNT);
  const area = subscriberDigits.slice(0, 3);
  const exchange = subscriberDigits.slice(3, 6);
  const subscriber = subscriberDigits.slice(6, 10);
  return `+1-${[area, exchange, subscriber].filter(Boolean).join("-")}`;
};

/**
 * Helper function to get the appropriate display text for audit trail actions.
 * If the name is "All", it returns an empty string to avoid displaying "All".
 *
 * @param {string} name - The name field from the event
 * @returns {string} - The appropriate text to display
 */
export const getAuditTrailDisplayText = (name?: string): string => {
  if (name === "All") {
    return "";
  }
  return name || "";
};

/**
 * Checks if a string is empty, contains only whitespace, or is null/undefined.
 * @param inputString - The string to validate.
 * @returns `true` if the string is empty, whitespace-only, null, or undefined; otherwise `false`.
 */
export const checkIfStringIsEmpty = (inputString?: string): boolean => {
  return (
    inputString === "" ||
    inputString?.trim() === "" ||
    inputString === null ||
    inputString === undefined
  );
};

/**
 * Returns whether a string is usable as non-empty content.
 *
 * Blank strings (empty, whitespace-only), ``null``, and ``undefined`` are
 * treated as invalid.
 *
 * @param inputString - The string to validate.
 * @returns `true` when the string has non-whitespace content; otherwise `false`.
 */
export const isValidString = (
  inputString?: string | null,
): inputString is string => !checkIfStringIsEmpty(inputString ?? undefined);

/**
 * Performs a case-insensitive substring match between text and query.
 * Returns true if the query is empty/whitespace or if the query appears as a substring in the text.
 * @param text - The text to search in
 * @param query - The search query
 * @returns `true` if query is empty or if query is found in text (case-insensitive); otherwise `false`
 */
export const substringMatch = (text: string, query: string): boolean => {
  if (!query.trim()) return true;

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase().trim();

  return normalizedText.includes(normalizedQuery);
};
